import type { EvidenceUploadMode } from 'api';
import { logger } from 'monitor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isEvidenceInScope, useOfflineEvidenceStore } from '../../stores/useOfflineEvidenceStore';

export interface OfflinePhoto {
  id: string;
  url: string;
}

export const useOfflineEvidencePhotos = ({
  mode,
  parentId,
}: {
  mode: EvidenceUploadMode;
  parentId: string | number;
}) => {
  const stringParentId = String(parentId);
  const {
    cleanupObjectUrlsByScope,
    evidenceFiles: allEvidenceFiles,
    getPhotoObjectUrlsByScope,
  } = useOfflineEvidenceStore();
  const [offlinePhotos, setOfflinePhotos] = useState<OfflinePhoto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadingRef = useRef(false);
  const pendingReloadRef = useRef(false);

  const scopedEvidenceCount = useMemo(
    () => allEvidenceFiles.filter((file) => isEvidenceInScope(file, { mode, parentId: stringParentId })).length,
    [allEvidenceFiles, mode, stringParentId]
  );

  const loadOfflinePhotos = useCallback(async () => {
    if (loadingRef.current) {
      pendingReloadRef.current = true;
      logger.debug('Scoped offline photo load already in progress, queueing reload', {
        mode,
        parentId: stringParentId,
      });
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);

    try {
      do {
        pendingReloadRef.current = false;
        setError(null);

        const photos = await getPhotoObjectUrlsByScope({ mode, parentId: stringParentId });
        logger.info('Loaded scoped offline photos successfully', {
          count: photos.length,
          mode,
          parentId: stringParentId,
          photoIds: photos.map((photo) => photo.id),
        });
        setOfflinePhotos(photos);
      } while (pendingReloadRef.current);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load offline photos');
      logger.error('Failed to load scoped offline photos', error, { mode, parentId: stringParentId });
      setError(error);
      setOfflinePhotos([]);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [getPhotoObjectUrlsByScope, mode, stringParentId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadOfflinePhotos();
    }, 0);

    return () => {
      clearTimeout(timer);
      cleanupObjectUrlsByScope({ mode, parentId: stringParentId });
    };
  }, [cleanupObjectUrlsByScope, loadOfflinePhotos, mode, stringParentId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadOfflinePhotos();
    }, 100);

    return () => clearTimeout(timer);
  }, [loadOfflinePhotos, scopedEvidenceCount]);

  return {
    offlineError: error,
    offlineIsLoading: isLoading,
    offlinePhotos,
    refetchOfflinePhotos: loadOfflinePhotos,
  };
};
